import { Injectable } from '@angular/core';
import { PhysicalAccessControlService } from '../../services/physical-access-control.service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  catchError,
  debounceTime,
  exhaustMap,
  mergeMap,
  share,
  switchMap,
  timeout,
  TimeoutError,
  withLatestFrom,
} from 'rxjs';
import { select, Store } from '@ngrx/store';
import { AppState } from '../app.state';
import { PhysicalAccessControlActions } from '@states/physical-access-control/physical-access-control.action-types';
import { SharedActions } from '@states/shared/shared.action-types';
import { UtilsService } from '../../edge/utils.service';
import { TranslateService } from '@ngx-translate/core';
import { PhysicalAccessControlModels } from '@models/physical-access-control.models';
import { LocationActions } from '@states/location/location.action-types';

@Injectable()
export class PhysicalAccessControlEffects {
  public getPhysicalAccessControlList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PhysicalAccessControlActions.getPhysicalAccessControl),
      withLatestFrom(
        this.store$.pipe(select((state) => state.physicalAccessControlState)),
        this.store$.pipe(select((state) => state.locationEditState)),
      ),
      switchMap(([, { filters }, { selectedLocationId }]) => {
        return this.physicalAccessControlService
          .getAll({
            locationId: selectedLocationId,
            ...filters,
          })
          .pipe(
            switchMap((res) => [PhysicalAccessControlActions.getPhysicalAccessControlSuccess({ documents: res })]),
            catchError(() => [PhysicalAccessControlActions.getPhysicalAccessControlFail()]),
            share(),
          );
      }),
    ),
  );

  public setFilter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PhysicalAccessControlActions.setQueryStringFilter),
      debounceTime(400),
      exhaustMap(() => [
        PhysicalAccessControlActions.resetEntities(),
        PhysicalAccessControlActions.getPhysicalAccessControl(),
      ]),
    ),
  );

  public startSave$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PhysicalAccessControlActions.startSavePhysicalAccessControl),
      switchMap(({ document }) => {
        if (document._id) {
          return [
            PhysicalAccessControlActions.setIsSaving({ isSaving: true }),
            PhysicalAccessControlActions.updatePhysicalAccessControl({ document }),
          ];
        }
        return [
          PhysicalAccessControlActions.setIsSaving({ isSaving: true }),
          PhysicalAccessControlActions.savePhysicalAccessControlRequest({ document }),
        ];
      }),
    ),
  );

  public saveExisting$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PhysicalAccessControlActions.updatePhysicalAccessControl),
      withLatestFrom(this.store$.pipe(select((state) => state.locationEditState))),
      switchMap(([{ document }, { selectedLocationId }]) => {
        return this.saveService(selectedLocationId, document).pipe(
          switchMap(() => [
            SharedActions.showMessage({
              success: this.translateService.instant('TOASTS.UPDATED_SUCCESS', {
                entity: this.translateService.instant('TOASTS.DEVICE'),
              }),
            }),
            PhysicalAccessControlActions.setIsSaving({ isSaving: false }),
            LocationActions.getPhysicalAccessControl(),
            PhysicalAccessControlActions.savePhysicalAccessControlRequestSuccess(),
          ]),
          catchError((response) => [
            PhysicalAccessControlActions.setIsSaving({ isSaving: false }),
            SharedActions.showMessage({ error: this.utilsService.errMessage(response) }),
            PhysicalAccessControlActions.savePhysicalAccessControlRequestFail(),
          ]),
        );
      }),
      share(),
    ),
  );

  public saveNew$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PhysicalAccessControlActions.savePhysicalAccessControlRequest),
      withLatestFrom(this.store$.pipe(select((state) => state.locationEditState))),
      switchMap(([{ document }, { selectedLocationId }]) => {
        return this.saveService(selectedLocationId, document).pipe(
          switchMap(() => [
            SharedActions.showMessage({
              success: this.translateService.instant('TOASTS.ENTITY_CREATED_SUCCESS', {
                entity: this.translateService.instant('TOASTS.DEVICE'),
              }),
            }),
            PhysicalAccessControlActions.setIsSaving({ isSaving: false }),
            LocationActions.getPhysicalAccessControl(),
            PhysicalAccessControlActions.savePhysicalAccessControlRequestSuccess(),
          ]),
          catchError((response) => [
            PhysicalAccessControlActions.setIsSaving({ isSaving: false }),
            SharedActions.showMessage({ error: this.utilsService.errMessage(response) }),
            PhysicalAccessControlActions.savePhysicalAccessControlRequestFail(),
          ]),
        );
      }),
      share(),
    ),
  );

  public startDelete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PhysicalAccessControlActions.startDeletePhysicalAccessControl),
      debounceTime(400),
      exhaustMap(({ id }) => [PhysicalAccessControlActions.deletePhysicalAccessControl({ id })]),
    ),
  );

  public delete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PhysicalAccessControlActions.deletePhysicalAccessControl),
      withLatestFrom(this.store$.pipe(select((state) => state.locationEditState))),
      switchMap(([{ id }, { selectedLocationId }]) => {
        return this.physicalAccessControlService.delete(selectedLocationId, id).pipe(
          switchMap(() => [
            SharedActions.showMessage({
              success: this.translateService.instant('TOASTS.ENTITY_HAS_BEEN_REMOVED', {
                entity: this.translateService.instant('TOASTS.DEVICE'),
              }),
            }),
            PhysicalAccessControlActions.deletePhysicalAccessControlSuccess({ id }),
            LocationActions.getPhysicalAccessControl(),
          ]),
          catchError((response) => [
            PhysicalAccessControlActions.deletePhysicalAccessControlFail(),
            SharedActions.showMessage({ error: this.utilsService.errMessage(response) }),
          ]),
          share(),
        );
      }),
    ),
  );

  public getOne$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PhysicalAccessControlActions.getOnePhysicalAccessControl),
      switchMap(({ id }) =>
        this.physicalAccessControlService.getOne(id).pipe(
          switchMap((res) => [PhysicalAccessControlActions.getOnePhysicalAccessControlSuccess({ document: res })]),
          catchError(() => [PhysicalAccessControlActions.getOnePhysicalAccessControlFail()]),
          share(),
        ),
      ),
    ),
  );

  public test$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PhysicalAccessControlActions.testPhysicalAccessControl),
      withLatestFrom(
        this.store$.pipe(select((state) => state.locationEditState)),
        this.store$.pipe(select((state) => state.physicalAccessControlState)),
      ),
      switchMap(([, { selectedLocationId }, { selectedPhysicalAccessControlDocument }]) => {
        const doc = selectedPhysicalAccessControlDocument;
        if (!doc?.edgeId) {
          return [
            SharedActions.showMessage({ error: this.translateService.instant('COMMON.ERROR') }),
            PhysicalAccessControlActions.testPhysicalAccessControlError(),
          ];
        }

        const request: PhysicalAccessControlModels.TestPhysicalAccessControlRequest = {
          locationId: selectedLocationId,
          edgeId: doc.edgeId,
          type: doc.type,
          host: doc.host,
          port: +doc.port,
          username: doc.username,
          password: doc.password,
        };

        return [PhysicalAccessControlActions.testPhysicalAccessControlSend({ request })];
      }),
      catchError(() => [PhysicalAccessControlActions.testPhysicalAccessControlError()]),
    ),
  );

  public testSend$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PhysicalAccessControlActions.testPhysicalAccessControlSend),
      share(),
      mergeMap(({ request }) =>
        this.physicalAccessControlService.testConnection(request).pipe(
          timeout(5000),
          switchMap(() => [PhysicalAccessControlActions.testPhysicalAccessControlSuccess()]),
          catchError((error) => {
            if (error instanceof TimeoutError) {
              return [
                PhysicalAccessControlActions.testPhysicalAccessControlError(),
                SharedActions.showMessage({ error: this.translateService.instant('EXTERNAL_STORAGE.ERRORS.TIME_OUT') }),
              ];
            }
            return [PhysicalAccessControlActions.testPhysicalAccessControlError()];
          }),
        ),
      ),
    ),
  );

  public testSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PhysicalAccessControlActions.testPhysicalAccessControlSuccess),
      mergeMap(() => [SharedActions.doNothing()]),
    ),
  );

  public testFail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PhysicalAccessControlActions.testPhysicalAccessControlError),
      mergeMap(() => [SharedActions.doNothing()]),
    ),
  );

  constructor(
    private physicalAccessControlService: PhysicalAccessControlService,
    private actions$: Actions,
    private store$: Store<AppState>,
    private utilsService: UtilsService,
    private translateService: TranslateService,
  ) {}

  private saveService(
    selectedLocationId: string,
    document: Partial<PhysicalAccessControlModels.PhysicalAccessControlDocument>,
  ) {
    if (!document._id) {
      return this.physicalAccessControlService.create({
        locationId: selectedLocationId,
        edgeId: document.edgeId,
        type: document.type,
        username: document.username,
        password: document.password,
        host: document.host,
        port: +document.port,
        name: document.name,
        tags: document.tags ?? [],
        mapConfig: document.mapConfig,
      });
    }
    return this.physicalAccessControlService.save({
      _id: document._id,
      locationId: selectedLocationId,
      edgeId: document.edgeId,
      type: document.type,
      username: document.username,
      password: document.password,
      host: document.host,
      port: +document.port,
      name: document.name,
      tags: document.tags ?? [],
      mapConfig: document.mapConfig,
    });
  }
}


